import React from 'react';
import { CheckCircle, Eye, EyeOff } from 'lucide-react';
import { UnstyledButton, Text } from '@mantine/core';
import './CompletedToggle.css';

export type CompletedFilter = 'today' | 'all' | 'hidden';

interface CompletedToggleProps {
  value: CompletedFilter;
  onChange: (value: CompletedFilter) => void;
}

export const CompletedToggle: React.FC<CompletedToggleProps> = ({ value, onChange }) => {
  const options = [
    { key: 'hidden' as const, label: 'None', icon: EyeOff, title: 'Hide all completed tasks' },
    { key: 'today' as const, label: 'Today', icon: CheckCircle, title: 'Show tasks completed today' },
    { key: 'all' as const, label: 'All', icon: Eye, title: 'Show all completed tasks' }
  ];

  const currentIndex = options.findIndex(option => option.key === value);
  const currentOption = options[currentIndex] || options[0]; // Fallback to first option if not found

  const handleClick = () => {
    const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % options.length;
    onChange(options[nextIndex].key);
  };

  return (
    <UnstyledButton
      onClick={handleClick}
      title={currentOption.title}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        padding: '6px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 500,
        color: 'var(--mantine-color-gray-6)',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease'
      }}
      styles={{
        root: {
          '&:hover': {
            backgroundColor: 'var(--mantine-color-gray-1)'
          },
          '&:focus': {
            outline: 'none',
            boxShadow: 'none',
            border: 'none'
          },
          '&:focusVisible': {
            outline: 'none',
            boxShadow: 'none',
            border: 'none'
          }
        }
      }}
    >
      <currentOption.icon size={14} />
      <Text size="xs" fw={500} style={{ whiteSpace: 'nowrap' }}>
        {currentOption.label}
      </Text>
    </UnstyledButton>
  );
};